import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import IconButton from '@mui/material/IconButton';
import NavArrowDownIcon from '@/assets/nav-arrow-down.svg?react';
import CancelIcon from '@/assets/cancel.svg?react';
import { useState } from 'react';
import UploadManagerItem from './UploadManagerItem';
import { UploadManagerSummary } from './UploadManagerSummary';
import { useUploadContext } from '@/contexts/UploadContext';
import {
  FEEDBACK_BUTTON_RESERVE,
  useReportBottomOverlay,
} from '@/lib/bottomOverlays';

// Gap between the panel and the edges of the viewport.
const MARGIN = 20;

const UploadManager = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [rootElement, setRootElement] =
    useState<HTMLDivElement | null>(null);
  // The panel keeps the corner; the feedback button stacks above it
  useReportBottomOverlay('uploadManager', 'right', rootElement);

  const {
    list,
    cancelUpload,
    cancelAllUploads,
    managerOpen,
    setManagerOpen,
  } = useUploadContext();

  const handleCancel = (fileId: string) => {
    cancelUpload(fileId);
  };

  const handleCancelAll = () => {
    cancelAllUploads();
  };

  return (
    <Root ref={setRootElement} open={managerOpen}>
      <Collapsible collapsed={collapsed}>
        <Content>
          <Title>
            <Label id="modal-title">
              <FormattedMessage
                id="uploads"
                defaultMessage="Uploads"
              />
            </Label>
            <IconButton
              sx={{ padding: 0, width: 28, height: 28 }}
              onClick={() => setCollapsed(!collapsed)}
              aria-label="Collapse uploads"
            >
              <StyledArrowIcon isOpen={collapsed} />
            </IconButton>
            <IconButton
              sx={{
                marginLeft: 1,
                padding: 0,
                width: 28,
                height: 28,
              }}
              onClick={() => setManagerOpen(false)}
              aria-label="Close uploads"
            >
              <CancelIcon />
            </IconButton>
          </Title>
          <Body>
            {list.map((upload) => (
              <UploadManagerItem
                key={upload.id}
                status={upload.status}
                fileName={upload.title}
                progress={upload.progress}
                message={upload.message}
                onCancel={() => handleCancel(upload.id)}
              />
            ))}
          </Body>
        </Content>
      </Collapsible>
      <Footer>
        <UploadManagerSummary
          onCancelAll={handleCancelAll}
          collapsed={collapsed}
          onExpand={() => setCollapsed(false)}
        />
      </Footer>
    </Root>
  );
};

const Root = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
  position: 'fixed',
  display: open ? 'flex' : 'none',
  flexDirection: 'column',
  bottom: MARGIN,
  right: MARGIN,
  // Never wider or taller than the viewport it floats over.
  width: `min(450px, calc(100vw - ${MARGIN * 2}px))`,
  backgroundColor: theme.palette.black,
  borderRadius: theme.radii.xs,
  boxShadow: theme.shadows[2],
  zIndex: 1000,
  color: theme.palette.white,
  overflow: 'hidden',
  // Leaves room for the feedback button above it, so the button is never
  // pushed off the top of a short viewport.
  maxHeight: `min(500px, max(160px, calc(100vh - ${
    MARGIN + FEEDBACK_BUTTON_RESERVE
  }px)))`,
}));

// Title and list collapse together, leaving only the footer. Animating the
// grid row between 0fr and 1fr sizes it to its content, so nothing has to be
// measured.
const Collapsible = styled('div', {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(({ collapsed }) => ({
  display: 'grid',
  gridTemplateRows: collapsed ? '0fr' : '1fr',
  flex: '1 1 auto',
  minHeight: 0,
  transition: 'grid-template-rows 0.3s ease-in-out',
}));

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
});

const Title = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: `${theme.spacing(3)} ${theme.spacing(2)} ${theme.spacing(
    3,
  )}`,

  svg: {
    width: '24px',
    height: '24px',

    path: {
      stroke: theme.palette.white,
    },
  },
}));

const Label = styled('div')({
  flexGrow: 1,
  fontSize: '24px',
  lineHeight: '28px',
  fontWeight: 600,
});

export const StyledArrowIcon = styled(NavArrowDownIcon, {
  shouldForwardProp(propName) {
    return propName !== 'isOpen';
  },
})<{ isOpen: boolean }>(({ isOpen }) => ({
  transition: 'transform 0.3s ease',
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
}));

const Body = styled('div')(({ theme }) => ({
  flexGrow: 1,
  flexShrink: 1,
  minHeight: 0,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  padding: `0 ${theme.spacing(3)}`,
  gap: theme.spacing(1),
}));

const Footer = styled('div')(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(1),
}));

export default UploadManager;
